import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from 'src/app/_models/toast.service';
import * as Chart from 'chart.js';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BankAssuranceService } from 'src/app/_services/bank-assurance.service';

@Component({
  selector: 'app-report-producer',
  templateUrl: './report-producer.component.html',
  styleUrls: ['./report-producer.component.scss']
})
export class ReportProducerComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('countsCanvas') countsCanvas: ElementRef<HTMLCanvasElement> | undefined;
  @ViewChild('caCanvas') caCanvas: ElementRef<HTMLCanvasElement> | undefined;
  @ViewChild('compCountsCanvas') compCountsCanvas: ElementRef<HTMLCanvasElement> | undefined;
  @ViewChild('compCaCanvas') compCaCanvas: ElementRef<HTMLCanvasElement> | undefined;

  public charts: any = {};
  public banks: any[] = [];
  public selectedBanks: string[] = [];

  // Période (Plage de dates + Dernier lot)
  public startDate: string = '';
  public endDate: string = '';
  public isLastTransfer: boolean = true;

  public loading = false;
  public showBanksDropdown = false;
  // Indicateur pour les charts (affichage loader) et message si pas de données
  public chartsLoading = false;
  public hasData = false;
  public noDataMessage: string | null = null;
  // `useMockData` removed

  public statusColors = ['#4a90e2', '#7bc043', '#f5a623', '#9b59b6'];
  public bankPalette = ['#483D8B', '#7bc043', '#f5a623', '#d9534f', '#5bc0de'];

  public statusMapping = [
    { key: 'created', short: 'CR', full: 'Créées' },
    { key: 'proposition_to_transfer', short: 'AP', full: 'Approuvées' },
    { key: 'proposition_transfer', short: 'TR', full: 'Transférées' },
    { key: 'paid', short: 'EN', full: 'Encaissées' }
  ];

  public counts: any[] = [];
  public ca: any[] = [];
  public bankGroupedData: any[] = [];
  public hasTransferredPolicies: boolean = false;

  constructor(private bankService: BankAssuranceService, private router: Router, private toastService: ToastService) { }

  ngOnInit(): void {
    const now = new Date();
    this.startDate = `${now.getFullYear()}-01-01`;
    this.endDate = now.toISOString().split('T')[0];
    this.loadBanks();
  }

  // --- LOGIQUE DE RÉCUPÉRATION DES BANQUES CONSERVÉE ---
  loadBanks() {
    const userId = parseInt(localStorage.getItem('ACTIVA_REDACTEUR_BANCASSURANCEUSERID') || '0', 10);
    const handleBanksArrayResponse = (res: any) => {
      let arr = Array.isArray(res) ? res : (res.data || res.items || []);
      let banksList: any[] = [];
      arr.forEach((b: any) => {
        if (b?.insuranceCompany?.banks && Array.isArray(b.insuranceCompany.banks)) {
          b.insuranceCompany.banks.forEach((bb: any) => { if (bb) banksList.push(bb); });
        } else if (b && b.id) { banksList.push(b); }
      });
      const unique = new Map<number, any>();
      banksList.forEach(b => { if (b && b.id) unique.set(b.id, b); });
      this.banks = Array.from(unique.values()).map(b => ({ id: b.id, name: b.name, code: b.companyCode || b.id }));

      // Sélection par défaut : première banque et chargement immédiat
      if (this.banks.length > 0 && this.selectedBanks.length === 0) {
        this.selectedBanks = [String(this.banks[0].id)];
        this.applyFilters();
      }
    };

    if (userId > 0) {
      this.bankService.authenticate(userId).subscribe({
        next: (authRes: any) => {
          if (authRes?.token) this.bankService.getBanksFromApim(authRes.token).subscribe({ next: handleBanksArrayResponse });
        }
      });
    } else {
      const token = localStorage.getItem('ACTIVA_REDACTEUR_TOKEN') || '';
      if (token) this.bankService.getBanksFromApim(token).subscribe({ next: handleBanksArrayResponse });
    }
  }

  // --- GESTION FILTRES ---
  public toggleBanksDropdown() { this.showBanksDropdown = !this.showBanksDropdown; }
  public isBankSelected(id: any) { return this.selectedBanks.includes(String(id)); }
  public toggleBank(id: any) {
    const key = String(id);
    const idx = this.selectedBanks.indexOf(key);
    idx === -1 ? this.selectedBanks.push(key) : this.selectedBanks.splice(idx, 1);
  }
  public isAllSelected() { return this.banks.length > 0 && this.selectedBanks.length === this.banks.length; }
  public selectAllBanks(sel: boolean) { this.selectedBanks = sel ? this.banks.map(b => String(b.id)) : []; }
  public selectedBanksLabel() {
    return this.selectedBanks.length === 0 ? 'Toutes les banques' : `${this.selectedBanks.length} banque(s)`;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) { if (!(event.target as HTMLElement).closest('.banks-select-wrapper')) this.showBanksDropdown = false; }

  public applyFilters() {
    this.loading = true;
    this.chartsLoading = true;
    this.noDataMessage = null;
    if (this.selectedBanks.length === 0 && this.banks.length === 0) {
      this.loading = false;
      return; // Attendre le chargement des banques pour éviter un appel sans bankIds
    }
    // mock mode removed — always call API

    const userId = localStorage.getItem('ACTIVA_REDACTEUR_BANCASSURANCEUSERID') || '0';

    // Authentifier d'abord pour obtenir le bon token
    this.bankService.authenticate(parseInt(userId)).subscribe({
      next: (authResponse: any) => {
        if (!authResponse?.token) {
          this.loading = false;
          console.error('No token from authentication');
          return;
        }

        // Construire les IDs de banques à passer à l'API
        const bankIds = this.selectedBanks.length > 0 ? this.selectedBanks.join(',') : this.banks.map(b => String(b.id)).join(',');

        // Format de dates pour l'API (DD/MM/YYYY)
        const startDateFormatted = this.formatDateForApi(this.startDate);
        const endDateFormatted = this.formatDateForApi(this.endDate);

        // Utiliser le token de l'authentification
        this.bankService.getPoliciesStatistics(startDateFormatted, endDateFormatted, bankIds, authResponse.token, undefined, this.isLastTransfer)
          .pipe(catchError((err) => { this.loading = false; this.chartsLoading = false; console.error('Error:', err); return of([]); }))
          .subscribe((res: any) => {
            const arr = Array.isArray(res) ? res : [];
            this.processStatisticsData(arr);
          });
      },
      error: (err) => {
        this.loading = false;
        console.error('Authentication error:', err);
      }
    });
  }

  private processStatisticsData(statisticsArray: any[]) {
    this.loading = false;
    this.chartsLoading = false;

    // Détecter absence de données
    const totalCountsSum = (statisticsArray || []).reduce((s, st) => s + (st.countCreated || 0) + (st.countApproved || 0) + (st.countTransferred || 0) + (st.countPaid || 0), 0);
    this.hasData = (statisticsArray && statisticsArray.length > 0 && totalCountsSum > 0);
    if (!this.hasData) {
      this.noDataMessage = 'Aucune donnée disponible pour la période sélectionnée.';
      this.counts = [];
      this.ca = [];
      this.bankGroupedData = [];
      this.destroyCharts();
      return;
    }

    // 1️⃣ DOUGHNUTS GLOBAUX - Agrégation de toutes les banques sélectionnées
    const totalCounts = {
      created: 0,
      approved: 0,
      transferred: 0,
      paid: 0
    };
    const totalCA = {
      created: 0,
      approved: 0,
      transferred: 0,
      paid: 0
    };

    statisticsArray.forEach((stat: any) => {
      totalCounts.created += (stat.countCreated || 0);
      totalCounts.approved += (stat.countApproved || 0);
      totalCounts.transferred += (stat.countTransferred || 0);
      totalCounts.paid += (stat.countPaid || 0);
      totalCA.created += (stat.createdTurnover || 0);
      totalCA.approved += (stat.potentialTurnover || 0);
      totalCA.transferred += (stat.expectedTurnover || 0);
      totalCA.paid += (stat.dueTurnover || 0);
    });

    // Indicate if there are transferred policies (used to block producer navigation on TR)
    this.hasTransferredPolicies = (totalCounts.transferred || 0) > 0;

    const totalCountsValue = totalCounts.created + totalCounts.approved + totalCounts.transferred + totalCounts.paid || 1;
    this.counts = [
      { label: 'CR', key: 'created', value: totalCounts.created, percent: ((totalCounts.created / totalCountsValue) * 100).toFixed(1) },
      { label: 'AP', key: 'proposition_to_transfer', value: totalCounts.approved, percent: ((totalCounts.approved / totalCountsValue) * 100).toFixed(1) },
      { label: 'TR', key: 'proposition_transfer', value: totalCounts.transferred, percent: ((totalCounts.transferred / totalCountsValue) * 100).toFixed(1) },
      { label: 'EN', key: 'paid', value: totalCounts.paid, percent: ((totalCounts.paid / totalCountsValue) * 100).toFixed(1) }
    ];

    const totalCAValue = totalCA.created + totalCA.approved + totalCA.transferred + totalCA.paid || 1;
    this.ca = [
      { label: 'CR', key: 'created', value: totalCA.created, percent: ((totalCA.created / totalCAValue) * 100).toFixed(1) },
      { label: 'AP', key: 'proposition_to_transfer', value: totalCA.approved, percent: ((totalCA.approved / totalCAValue) * 100).toFixed(1) },
      { label: 'TR', key: 'proposition_transfer', value: totalCA.transferred, percent: ((totalCA.transferred / totalCAValue) * 100).toFixed(1) },
      { label: 'EN', key: 'paid', value: totalCA.paid, percent: ((totalCA.paid / totalCAValue) * 100).toFixed(1) }
    ];

    // 2️⃣ HISTOGRAMMES - Données par banque (groupingKey = bankId)
    this.bankGroupedData = statisticsArray.map((stat: any, index: number) => {
      const bankId = parseInt(stat.groupingKey);
      const bankInfo = this.banks.find(b => b.id == bankId);
      const bankName = bankInfo?.name || `Banque ${bankId}`;

      return {
        label: bankName,
        bankId: bankInfo?.id || bankId,
        color: this.bankPalette[index % this.bankPalette.length],
        counts: [
          stat.countCreated || 0,
          stat.countApproved || 0,
          stat.countTransferred || 0,
          stat.countPaid || 0
        ],
        ca: [
          stat.createdTurnover || 0,      // CA pour Créées
          stat.potentialTurnover || 0,    // CA pour Approuvées
          stat.expectedTurnover || 0,     // CA pour Transférées
          stat.dueTurnover || 0           // CA pour Payées
        ]
      };
    });

    setTimeout(() => this.updateCharts(), 100);
  }

  // --- GRAPH LOGIC ---
  private formatAbbr(val: any) {
    const n = Number(val);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
    return n;
  }

  public updateCharts() {
    this.destroyCharts();

    const calloutPlugin = {
      afterDraw: (chart: any) => {
        const ctx = chart.ctx; ctx.save();
        chart.data.datasets.forEach((dataset: any, i: number) => {
          const meta = chart.getDatasetMeta(i);
          meta.data.forEach((element: any, index: number) => {
            const dataVal = dataset.data[index]; if (dataVal === 0) return;
            const view = element._model;
            const midAngle = view.startAngle + (view.endAngle - view.startAngle) / 2;
            const x1 = view.x + Math.cos(midAngle) * view.outerRadius;
            const y1 = view.y + Math.sin(midAngle) * view.outerRadius;
            const x2 = view.x + Math.cos(midAngle) * (view.outerRadius + 20);
            const y2 = view.y + Math.sin(midAngle) * (view.outerRadius + 20);
            ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
            ctx.strokeStyle = view.backgroundColor; ctx.lineWidth = 2; ctx.stroke();
            const percent = ((dataVal / dataset.data.reduce((a: any, b: any) => a + b, 0)) * 100).toFixed(1);
            const displayVal = chart.canvas.id === 'ca' ? this.formatAbbr(dataVal) : dataVal;
            ctx.font = 'bold 10px sans-serif'; ctx.fillStyle = '#333';
            ctx.textAlign = Math.cos(midAngle) > 0 ? 'left' : 'right';
            ctx.fillText(`${chart.data.labels[index]}: ${displayVal} (${percent}%)`, x2 + (Math.cos(midAngle) > 0 ? 5 : -5), y2);
          });
        });
        ctx.restore();
      }
    };

    const doughnutOpt = { responsive: true, maintainAspectRatio: false, cutoutPercentage: 65, legend: { display: false }, layout: { padding: { left: 50, right: 50, top: 30, bottom: 50 } } };
    const barOpt = {
      responsive: true, maintainAspectRatio: false, barPercentage: 0.7, categoryPercentage: 0.6,
      scales: {
        yAxes: [{ gridLines: { display: false }, ticks: { callback: (v: any) => this.formatAbbr(v), beginAtZero: true } }],
        xAxes: [{ gridLines: { display: false } }]
      },
      legend: { position: 'bottom', labels: { boxWidth: 12 } }
    };

    if (this.countsCanvas) {
      this.charts.counts = new Chart(this.countsCanvas.nativeElement, {
        type: 'doughnut', data: { labels: this.counts.map(c => c.label), datasets: [{ data: this.counts.map(c => c.value), backgroundColor: this.statusColors }] },
        options: doughnutOpt, plugins: [calloutPlugin]
      });
      this.charts.counts.canvas.id = 'counts';
    }

    if (this.caCanvas) {
      this.charts.ca = new Chart(this.caCanvas.nativeElement, {
        type: 'doughnut', data: { labels: this.ca.map(c => c.label), datasets: [{ data: this.ca.map(c => c.value), backgroundColor: this.statusColors }] },
        options: doughnutOpt, plugins: [calloutPlugin]
      });
      this.charts.ca.canvas.id = 'ca';
    }

    if (this.compCountsCanvas) {
      this.charts.compCounts = new Chart(this.compCountsCanvas.nativeElement, {
        type: 'bar',
        data: { labels: this.statusMapping.map(s => s.short), datasets: this.bankGroupedData.map(b => ({ label: b.label, data: b.counts, backgroundColor: b.color, bankId: b.bankId })) },
        options: barOpt
      });
    }

    if (this.compCaCanvas) {
      this.charts.compCa = new Chart(this.compCaCanvas.nativeElement, {
        type: 'bar',
        data: { labels: this.statusMapping.map(s => s.short), datasets: this.bankGroupedData.map(b => ({ label: b.label, data: b.ca, backgroundColor: b.color, bankId: b.bankId })) },
        options: barOpt
      });
    }
  }

  public handleChartClick(event: MouseEvent, chartKey: string) {
    const chart = this.charts[chartKey];
    if (!chart) return;
    const activePoints = chart.getElementsAtEvent(event);
    if (activePoints.length > 0) {
      const elementIndex = activePoints[0]._index;
      const datasetIndex = activePoints[0]._datasetIndex;
      const statusObj = this.statusMapping[elementIndex];
      const statusKey = statusObj.key;

      let targetBankIds = this.selectedBanks.join(',');
      if (chart.config.type === 'bar') targetBankIds = String(chart.data.datasets[datasetIndex].bankId);

      const toTargetDate = (d: string) => {
        if (!d) return d;
        const parts = d.split('-');
        return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
      };

      const queryParams: any = {
        policyStatus: statusKey,
        startDate: toTargetDate(this.startDate),
        endDate: toTargetDate(this.endDate),
        bankIds: targetBankIds
      };

      this.router.navigate(['/welcome/menu/bancassurance/producteur-policy-management'], { queryParams });
    }
  }

  private generateMockPolicies() {
    return [
      { agencyCode: 'MOCK1', policyStatus: 'created', totalAmount: 500000 },
      { agencyCode: 'MOCK1', policyStatus: 'paid', totalAmount: 1500000 },
      { agencyCode: 'MOCK2', policyStatus: 'proposition_to_transfer', totalAmount: 800000 }
    ];
  }

  private generateMockStatistics() {
    return [
      {
        customerID: 40,
        countCreated: 7,
        countApproved: 2,
        countTransferred: 12,
        countPaid: 0,
        potentialTurnover: 10207.8,
        expectedTurnover: 398831.62,
        dueTurnover: 0
      },
      {
        customerID: 129097,
        countCreated: 0,
        countApproved: 3,
        countTransferred: 0,
        countPaid: 0,
        potentialTurnover: 112035.375,
        expectedTurnover: 0,
        dueTurnover: 0
      }
    ];
  }

  private formatDateForApi(dateStr: string): string {
    if (!dateStr) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }
    // dateStr est déjà au format YYYY-MM-DD, on le retourne tel quel pour l'API
    return dateStr;
  }

  private destroyCharts() { Object.keys(this.charts).forEach(k => { if (this.charts[k]) this.charts[k].destroy(); }); }
  ngAfterViewInit() { this.updateCharts(); }
  ngOnDestroy() { this.destroyCharts(); }
}
